import { FC } from "react";
import { useRecordContext } from "react-admin";
import { Avatar, Stack, Typography } from "@mui/material";

type AvatarFieldProps = {
  /**
   * The key name on the record that provides the user's full name (used for initials and label)
   */
  nameSource: string;
  /**
   * Optional key name for the user's avatar image URL on the record (defaults to "avatar")
   */
  imageSource?: string;
  /**
   * Avatar size in pixels (defaults to 32)
   */
  size?: number;
};

/**
 * Utility to generate a background/text color combo from a given string (typically the user's name).
 * Ensures visual consistency when no image is present.
 */
export const stringToColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;
  const saturation = 40;
  const lightness = 70;

  const bgColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  const textColor = "#333";
  return { bgColor, textColor };
};

/**
 * A compact display field that shows a user avatar (with fallback to initials),
 * and the associated name from the record. Useful in lists or profile summaries.
 *
 * This component uses `useRecordContext()` to access the current record
 * and gracefully handles missing avatars.
 */
export const AvatarField: FC<AvatarFieldProps> = ({ nameSource, imageSource = "avatar", size = 32 }) => {
  const record = useRecordContext();
  if (!record) return null;

  const name = record[nameSource] || "";
  const avatarUrl = record[imageSource];
  const initials = name
    .trim()
    .split(/\s+/) // split on 1+ spaces
    .filter(Boolean) // remove empty strings
    .map((n: string) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const { bgColor, textColor } = stringToColor(name);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Avatar
        src={avatarUrl}
        alt={name}
        sx={{
          width: size,
          height: size,
          fontSize: size * 0.5,
          bgcolor: avatarUrl ? undefined : bgColor,
          color: avatarUrl ? undefined : textColor,
        }}
      >
        {initials}
      </Avatar>
      <Typography variant="body2" color="text.primary" noWrap>
        {name}
      </Typography>
    </Stack>
  );
};
